// One line's detail (Lane 1335), in the mockup's order: Inventory · Vendor
// stock and lead time · Cost per piece · Price history · Bought / Quoted /
// Sold · the XMEK parts for this line (pick) · Warnings. Every number comes
// from the WS6 API. A box whose read failed says "could not load"; data that
// is not in Wingman says so; neither is ever shown as zero or "none".
import React, { useCallback, useEffect, useRef, useState } from 'react';
import QuotingChart from './QuotingChart';
import {
  FILL_FLAG_ORDER, QUIET_FLAGS, blankReason, lineFlagText, money, pct, perUnit, qty, shortDate, tiersText,
} from './quotingText';

const FOUND_BY = { listed_by_customer: "Listed in the customer's file", exact_part_number: 'Same part number', loose_part_number: 'Part number spelled differently', same_size: 'Same size' };
// Parts Wingman's AI search suggests (Lane 1391): its own reading of the part
// names, never filled in; the quoter checks and picks.
const SUGGESTED_FIT = {
  same_size: 'Suggested: same size',
  same_size_other_material: 'Suggested: same size, other material',
  close_size: 'Suggested: close size',
  same_type_other_size: 'Suggested: same type, other size',
};

function Box({ title, note, children }) {
  return (
    <div className="qtng-box">
      <h3 className="qtng-boxh"><span>{title}</span>{note && <em>{note}</em>}</h3>
      {children}
    </div>
  );
}
const Empty = ({ children }) => <div className="qtng-empty">{children}</div>;

function Inventory({ stock }) {
  if (!stock || stock.state === 'unknown') return <Box title="Inventory"><Empty>Could not load the inventory.</Empty></Box>;
  if (stock.state === 'no_file') return <Box title="Inventory"><Empty>No inventory file in Wingman yet.</Empty></Box>;
  const note = <span className={stock.old ? 'qtng-old' : ''}>as of {shortDate(stock.as_of)}</span>;
  if (stock.state === 'not_in_file') {
    return <Box title="Inventory" note={note}><Empty>Not in the {shortDate(stock.as_of)} inventory file.</Empty></Box>;
  }
  return (
    <Box title="Inventory" note={note}>
      <table className="qtng-t">
        <thead><tr><th>Warehouse</th><th className="qtng-r">On hand</th><th className="qtng-r" title="On hand less the quantity already promised to open orders, from XMEK's inventory file">Available after open orders</th>
          {/* Units used, as XMEK's inventory file states them (Ryan, 2026-09-30). */}
          <th className="qtng-r" title="Units used in the last 3 months, from XMEK's inventory file">Used 3 mo</th>
          <th className="qtng-r" title="Units used in the last 12 months, from XMEK's inventory file">Used 12 mo</th></tr></thead>
        <tbody>
          {(stock.warehouses || []).map((w) => (
            <tr key={w.warehouse}><td>{w.warehouse}</td><td className="qtng-r qtng-num">{qty(w.on_hand)}</td>
              <td className="qtng-r qtng-num">{qty(w.available)}</td>
              <td className="qtng-r qtng-num">{w.used_3mo == null ? '–' : qty(w.used_3mo)}</td>
              <td className="qtng-r qtng-num">{w.used_12mo == null ? '–' : qty(w.used_12mo)}</td></tr>
          ))}
          <tr className="qtng-total"><td>Total</td><td className="qtng-r qtng-num">{qty(stock.on_hand)}</td>
            <td className="qtng-r qtng-num">{qty(stock.available)}</td>
            <td className="qtng-r qtng-num">{stock.used_3mo == null ? '–' : qty(stock.used_3mo)}</td>
            <td className="qtng-r qtng-num">{stock.used_12mo == null ? '–' : qty(stock.used_12mo)}</td></tr>
        </tbody>
      </table>
    </Box>
  );
}

function leadTime(v) {
  if (v.lead_time == null) return '–';
  return v.lead_time_unit ? `${qty(v.lead_time)} ${v.lead_time_unit}` : qty(v.lead_time);
}

function Vendors({ vendors, main }) {
  const title = 'Vendor stock and lead time';
  if (main?.state === 'unknown' || (vendors == null && main?.state !== 'no_file')) {
    return <Box title={title}><Empty>Could not load the vendor file.</Empty></Box>;
  }
  if (main?.state === 'no_file') return <Box title={title}><Empty>No vendor file in Wingman yet.</Empty></Box>;
  if (!vendors.length) return <Box title={title}><Empty>Not in the vendor file (Parker, Parco, Global O-Ring only).</Empty></Box>;
  const unitless = vendors.some((v) => v.lead_time != null && !v.lead_time_unit);
  return (
    <Box title={title} note={unitless ? 'lead time unit (days or weeks) not stated in the file' : null}>
      <table className="qtng-t">
        <thead><tr><th>Vendor</th><th className="qtng-r">Available</th><th className="qtng-r">Lead time</th><th className="qtng-r">As of</th></tr></thead>
        <tbody>
          {vendors.map((v) => (
            <tr key={v.vendor_code}>
              <td>{v.vendor_name || `Vendor ${v.vendor_code}`}{v.primary ? ' (primary)' : ''}</td>
              <td className="qtng-r qtng-num">{v.backorder
                ? <span className="qtng-bad">Backorder ({qty(v.available)})</span> : qty(v.available)}</td>
              <td className={`qtng-r qtng-num ${v.lead_time_old ? 'qtng-old' : ''}`}>{leadTime(v)}</td>
              <td className={`qtng-r qtng-num ${v.available_old ? 'qtng-old' : ''}`}>{shortDate(v.available_as_of)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {vendors.some((v) => v.conflicting_totals) && <div className="qtng-note">Warehouses in the vendor file disagree on this total; the latest one is shown.</div>}
    </Box>
  );
}

function costLabel(r) {
  switch (r.kind) {
    case 'invoice_last_sale': return 'XMEK cost (invoice, last sale)';
    case 'last_paid_po': return `Last price paid on a PO${r.supplier_name ? `, ${r.supplier_name}` : ''}`;
    case 'parker_net': return `Parker net to XMEK, ${r.vendor_part_number}, "${r.price_column}"${r.sold_to_account ? `, sold-to ${r.sold_to_account}` : ''}`;
    case 'parker_distributor': return `Parker distributor list, ${r.vendor_part_number}`;
    case 'parco': return `Parco quote, ${r.vendor_part_number}${r.expired ? `, expired ${shortDate(r.expiry_date)}` : ''}`;
    default: return r.kind;
  }
}

// The latest sale's cost was out of line with the part's other sales, so the
// XMEK cost comes from the newest sale in line (Ryan 2026-09-30, Lane 1357).
const setAsideText = (c) => (c?.set_aside
  ? `The ${shortDate(c.set_aside.invoice_date)} sale (${qty(c.set_aside.shipped_quantity)} at ${money(c.set_aside.unit_cost)} cost each) is far from this part's other sales, so XMEK cost uses the ${shortDate(c.date)} sale. Check that sale's quantity.`
  : null);

// A price list with several rows for the part (Parker net: 3 sold-to
// accounts x "New Price"/"Price" x 2 quantities) shows as one line with its
// lowest and highest price until "Show all" (Ryan, 2026-09-30, Lane 1357).
const LIST_NAMES = { parker_net: 'Parker net to XMEK', parker_distributor: 'Parker distributor list', parco: 'Parco quote' };
function listGroups(listRows) {
  const groups = [];
  listRows.forEach((r) => {
    const key = `${r.kind}|${r.vendor_part_number || ''}`;
    const g = groups.find((x) => x.key === key);
    if (g) g.rows.push(r); else groups.push({ key, rows: [r] });
  });
  return groups;
}
const span = (values, fmt) => {
  const v = values.filter((x) => x != null).map(Number);
  if (!v.length) return '';
  const lo = Math.min(...v); const hi = Math.max(...v);
  return lo === hi ? fmt(lo) : `${fmt(lo)} to ${fmt(hi)}`;
};

function Costs({ rows, uom, xmekCost }) {
  const [all, setAll] = useState(false);
  const per = !uom || ['each', 'ea', 'pc', 'piece'].includes(String(uom).toLowerCase()) ? 'Per piece' : `Per ${uom}`;
  const listRows = (rows || []).filter((r) => r.kind && !['invoice_last_sale', 'last_paid_po', 'price_lists'].includes(r.kind));
  const listsFailed = (rows || []).some((r) => r.kind === 'price_lists' && r.state === 'unknown');
  const title = per === 'Per piece' ? 'Cost per piece' : `Cost ${per.toLowerCase()}`;
  if (xmekCost?.state === 'unknown') return <Box title={title}><Empty>Could not load the cost.</Empty></Box>;
  const own = (rows || []).filter((r) => ['invoice_last_sale', 'last_paid_po'].includes(r.kind));
  const groups = listGroups(listRows);
  const foldable = groups.some((g) => g.rows.length > 1);
  const shown = [...own, ...groups.flatMap((g) => (all || g.rows.length === 1 ? g.rows : [{
    kind: g.rows[0].kind, summary: true,
    // An expired list says so on the folded line too (Codex 1357).
    label: `${LIST_NAMES[g.rows[0].kind] || g.rows[0].kind}, ${g.rows[0].vendor_part_number}: ${g.rows.length} prices${
      g.rows.some((r) => r.expired) ? `, expired ${shortDate(g.rows.find((r) => r.expired).expiry_date)}` : ''}`,
    price: span(g.rows.map((r) => r.unit_price), money),
    date: g.rows[0].date || g.rows[0].list_date,
    minQty: span(g.rows.map((r) => r.min_qty), qty),
  }]))];
  return (
    <Box title={title}>
      {shown.length ? (
        <table className="qtng-t qtng-fixed">
          <colgroup><col /><col style={{ width: 120 }} /><col style={{ width: 100 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /></colgroup>
          <thead><tr><th>Source</th><th className="qtng-r">{per}</th><th className="qtng-r">Date</th><th className="qtng-r">Qty</th><th className="qtng-r">List min qty</th></tr></thead>
          <tbody>
            {shown.map((r, i) => (
              r.summary ? (
                <tr key={i}>
                  <td className="qtng-cut" title={r.label}>{r.label}</td>
                  <td className="qtng-r qtng-num">{r.price}</td>
                  <td className="qtng-r qtng-num">{shortDate(r.date)}</td>
                  <td className="qtng-r qtng-num" />
                  <td className="qtng-r qtng-num">{r.minQty}</td>
                </tr>
              ) : (
                <tr key={i} className={r.total ? 'qtng-total' : ''}>
                  <td className="qtng-cut" title={costLabel(r)}>{costLabel(r)}</td>
                  <td className="qtng-r qtng-num">{r.kind === 'last_paid_po' ? `${money(r.unit_price, r.currency)}${perUnit(r.uom)}` : money(r.unit_price)}</td>
                  <td className="qtng-r qtng-num">{shortDate(r.date || r.list_date)}</td>
                  <td className="qtng-r qtng-num">{r.qty_raw || ''}</td>
                  <td className="qtng-r qtng-num">{r.min_qty != null ? qty(r.min_qty) : ''}</td>
                </tr>)
            ))}
          </tbody>
        </table>
      ) : <Empty>No cost for this part in Wingman.</Empty>}
      {foldable && (
        <div className="qtng-note"><button type="button" className="qtng-link" onClick={() => setAll((x) => !x)}>
          {all ? 'Show fewer list prices' : `Show all ${listRows.length} list prices`}</button></div>
      )}
      {setAsideText(xmekCost) && <div className="qtng-note qtng-old">{setAsideText(xmekCost)}</div>}
      {xmekCost?.far_from_last_paid && <div className="qtng-note qtng-old">The last sale's invoice cost is far from the last PO price. Check that sale's quantity.</div>}
      {listsFailed && <div className="qtng-note">Could not load the price lists.</div>}
      {!listsFailed && !listRows.length && <div className="qtng-note">No Parker or Parco list price is linked to this part.</div>}
    </Box>
  );
}

// Bought, Quoted and Sold share one shape (Ryan, 2026-09-27): the same four
// columns, a header row even when empty, and the "price has not changed" note
// as a flag at the bottom, so the three boxes line up side by side.
const HISTORY_COLUMNS = (who) => [['Date'], [who], ['Qty', true], ['Price', true]];

function HistoryTable({ whoLabel, children, empty }) {
  return (
    <div className="qtng-scroll">
      <table className="qtng-t qtng-fixed">
        <colgroup><col style={{ width: 84 }} /><col /><col style={{ width: 66 }} /><col style={{ width: 94 }} /></colgroup>
        <thead><tr>{HISTORY_COLUMNS(whoLabel).map(([label, right]) => <th key={label} className={right ? 'qtng-r' : ''}>{label}</th>)}</tr></thead>
        <tbody>{children || <tr><td colSpan={4} className="qtng-empty">{empty}</td></tr>}</tbody>
      </table>
    </div>
  );
}

// Markup calculator (Ryan, 2026-09-27): not saved. Margin means what the
// Margin column means, profit as a share of the price, so
// price = cost / (1 - margin) and margin = (price - cost) / price.
const CALC_MARGINS = [30, 40, 50, 60];
// Cents from $1, tenths of a cent below $1, and more places below a cent so a
// tiny cost never rounds to a $0 price.
const round = (v) => (v >= 1 ? Math.round(v * 100) / 100 : v >= 0.01 ? Math.round(v * 1000) / 1000 : Number(v.toPrecision(3)));
export const priceAtMargin = (cost, m) => (m < 100 ? round(cost / (1 - m / 100)) : null);
export const marginAtPrice = (cost, p) => (p > 0 ? ((p - cost) / p) * 100 : null);

function calcCosts(detail) {
  const out = [];
  // Only US dollars per piece: a cost in another currency or per box would
  // give a wrong dollar price per piece, so it is not offered (Codex 1347).
  // The part's own unit (each, or BOX...) is the unit every basis must share.
  const unit = perUnit(detail.unit_of_measure);
  const perPieceUsd = (r) => !(r.currency && r.currency !== 'USD') && perUnit(r.uom) === unit;
  const x = detail.xmek_cost;
  // When XMEK cost fell back to the last PO price, the PO row below carries it
  // with its currency and unit, so it is not added twice.
  if (x?.state === 'found' && x.basis !== 'last_paid_po' && Number(x.unit_cost) > 0) {
    out.push({ key: 'xmek', label: 'XMEK cost', value: Number(x.unit_cost) });
  }
  (detail.cost_rows || []).forEach((r, i) => {
    const v = Number(r.unit_price);
    if (!(v > 0)) return;
    if (!perPieceUsd(r)) return;
    // Parker net rows do not state their price basis, so they are not offered.
    if (!['last_paid_po', 'parco'].includes(r.kind)) return;
    out.push({ key: `${r.kind}-${i}`, label: costLabel(r), value: v });
  });
  return out;
}

function PriceCalc({ detail, line, who }) {
  const costs = calcCosts(detail);
  const [costKey, setCostKey] = useState(costs[0]?.key);
  const [margin, setMargin] = useState('');
  const [price, setPrice] = useState('');
  const basis = costs.find((c) => c.key === costKey) || costs[0];
  // A refreshed cost recalculates the price from the margin, so the two
  // inputs never show a margin the price no longer gives (Codex 1347).
  const costValue = basis?.value;
  useEffect(() => {
    const m = Number(margin);
    if (costValue && margin !== '' && Number.isFinite(m) && m < 100) setPrice(String(priceAtMargin(costValue, m)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costValue]);
  // Price breaks: listed above the calculator; one calculator serves them all
  // (Ryan, 2026-09-28, option A), and there is no single line total.
  const tierLine = (line.quantity_scenarios || []).length > 1 && (
    <div className="qtng-calc-tiers"><span className="qtng-mut">Quantities to quote</span>{tiersText(line.quantity_scenarios)}</div>);
  if (!basis) {
    return (
      <Box title="Price calculator">
        {tierLine && <div className="qtng-calc">{tierLine}</div>}
        <Empty>No cost for this part, so there is nothing to calculate from.</Empty>
      </Box>
    );
  }
  const cost = basis.value;
  const unitWord = perUnit(detail.unit_of_measure) ? detail.unit_of_measure : 'piece';
  const scen = (line.quantity_scenarios || []).length === 1 ? line.quantity_scenarios[0] : null;
  // A line total only when the RFQ quantity is in the price's unit.
  const single = scen && scen.kind === 'single' && perUnit(scen.uom) === perUnit(detail.unit_of_measure)
    ? Number(scen.qty) : null;
  const p = Number(price);
  const total = single && p > 0 ? single * p : null;
  const onMargin = (v) => {
    setMargin(v);
    const m = Number(v);
    setPrice(v === '' || !Number.isFinite(m) || m >= 100 ? '' : String(priceAtMargin(cost, m)));
  };
  const onPrice = (v) => {
    setPrice(v);
    const m = marginAtPrice(cost, Number(v));
    setMargin(v === '' || m == null ? '' : String(Math.round(m * 10) / 10));
  };
  const pickCost = (key) => {
    setCostKey(key);
    const next = costs.find((c) => c.key === key)?.value;
    const m = Number(margin);
    if (next && margin !== '' && m < 100) setPrice(String(priceAtMargin(next, m)));
  };
  const c = line.facts?.customer;
  return (
    <Box title="Price calculator" note="not saved">
      <div className="qtng-calc">
        {tierLine}
        <div className="qtng-calc-basis">
          <span className="qtng-mut">From</span>
          {costs.length > 1 ? (
            <select className="qtng-select" value={basis.key} onChange={(e) => pickCost(e.target.value)} aria-label="Cost to calculate from">
              {costs.map((o) => <option key={o.key} value={o.key}>{o.label}: {money(o.value)}</option>)}
            </select>
          ) : <b>{basis.label}: {money(cost)}</b>}
        </div>
        <table className="qtng-t qtng-calc-t">
          <tbody>
            <tr><th>Margin</th>{CALC_MARGINS.map((m) => <th key={m} className="qtng-r">{m}%</th>)}</tr>
            <tr><td className="qtng-mut">Price per {unitWord}</td>{CALC_MARGINS.map((m) => (
              <td key={m} className="qtng-r qtng-num">
                <button type="button" className="qtng-link" title={`Use ${m}%`} onClick={() => onMargin(String(m))}>{money(priceAtMargin(cost, m))}</button>
              </td>))}</tr>
          </tbody>
        </table>
        <div className="qtng-calc-row">
          <label>Margin <input className="qtng-input qtng-calc-in" inputMode="decimal" value={margin} onChange={(e) => onMargin(e.target.value)} aria-label="Margin percent" />%</label>
          <span className="qtng-mut" aria-hidden="true">⇄</span>
          <label>Price $<input className="qtng-input qtng-calc-in" inputMode="decimal" value={price} onChange={(e) => onPrice(e.target.value)} aria-label={`Price per ${unitWord}`} /></label>
          {total != null && <span>Line total ({qty(single)} {unitWord === 'piece' ? 'pcs' : unitWord}): <b className="qtng-num">{money(total)}</b></span>}
          {c?.state === 'found' && <span className="qtng-mut">Last sold to {who}: {money(c.last_price)} ({pct(c.margin_pct)})</span>}
        </div>
        {Number(margin) >= 100 && <div className="qtng-err">A margin must be under 100%.</div>}
        {((margin !== '' && !Number.isFinite(Number(margin))) || (price !== '' && !(Number(price) > 0))) && (
          <div className="qtng-err">Enter a number (a price above $0).</div>)}
      </div>
      <div className="qtng-note">Margin is profit as a share of the price, the same as the Margin column.</div>
    </Box>
  );
}

// XMEK's past quotes of this part (Lane 1357): one row per quoted part with
// its lowest-quantity break, "+N" opens the others; "Won" only for code 00
// (XMEK's own outcome), every other code shows none. Prices as quoted, read
// like the sales data: US dollars per the part's own unit (Ryan 2026-09-30).
function QuotedRow({ q, who, multi }) {
  const [open, setOpen] = useState(false);
  const shown = q.shown == null ? null : q.breaks[q.shown];
  const others = q.breaks.filter((_, i) => i !== q.shown);
  const qtyText = (b) => (b.quantity != null ? qty(b.quantity) : (b.quantity_as_written || '–'));
  const tip = [q.quote_number && `Quote ${q.quote_number}`,
    q.this_account && `Quoted to ${who}${multi ? ` (account ${q.customer_code})` : ''}`,
    !q.won && 'Outcome not known'].filter(Boolean).join(' · ');
  return (
    <>
      <tr className={q.this_account ? 'qtng-mine' : ''} title={tip}>
        <td className="qtng-num">{shortDate(q.quote_date)}</td>
        <td className="qtng-cut" title={q.customer_name || ''}>{q.won && <span className="qtng-won">Won</span>}{q.customer_name || q.customer_code || '–'}</td>
        <td className="qtng-r qtng-num">{shown ? qtyText(shown) : '–'}
          {others.length > 0 && <button type="button" className="qtng-link qtng-small" style={{ marginLeft: 4 }} aria-expanded={open}
            onClick={() => setOpen((o) => !o)} title={`${others.length} more price ${others.length === 1 ? 'break' : 'breaks'}`}>+{others.length}</button>}</td>
        <td className="qtng-r qtng-num">{shown && shown.unit_price != null ? money(shown.unit_price) : <span className="qtng-mut">not priced</span>}</td>
      </tr>
      {open && others.map((b, i) => (
        <tr key={i} className={`qtng-sub ${q.this_account ? 'qtng-mine' : ''}`}>
          <td /><td className="qtng-mut">price break</td>
          <td className="qtng-r qtng-num">{qtyText(b)}</td>
          <td className="qtng-r qtng-num">{b.unit_price != null ? money(b.unit_price) : <span className="qtng-mut">not priced</span>}</td>
        </tr>
      ))}
    </>
  );
}

function Quoted({ page, who, multi, onMore, loadingMore }) {
  if (page == null) return <Box title="Quoted"><Empty>Could not load.</Empty></Box>;
  if (page.state === 'not_loaded') {
    return (
      <Box title="Quoted">
        <HistoryTable whoLabel="Customer" empty="Quote history is not in Wingman yet. It comes when XMEK sends its quote export." />
      </Box>
    );
  }
  const count = page.total > page.loaded ? `newest ${page.loaded.toLocaleString()} of ${page.total.toLocaleString()}` : `${page.total}`;
  return (
    <Box title="Quoted" note={count}>
      <HistoryTable whoLabel="Customer" empty="No quotes for this part in XMEK's quote export.">
        {page.items.length ? page.items.map((q) => <QuotedRow key={q.quote_history_line_id} q={q} who={who} multi={multi} />) : null}
      </HistoryTable>
      <div className="qtng-foot">
        {page.next_cursor && (
          <div className="qtng-more"><button type="button" className="qtng-btn qtng-btn--small" disabled={loadingMore} onClick={onMore}>
            {loadingMore ? 'Loading…' : 'Show more'}</button></div>
        )}
        <div className="qtng-note">From XMEK's quote export, {shortDate(page.date_min)} to {shortDate(page.date_max)}.</div>
      </div>
    </Box>
  );
}

function History({ title, whoLabel, page, row, note, onMore, loadingMore }) {
  if (page == null) return <Box title={title}><Empty>Could not load.</Empty></Box>;
  const count = page.total > page.loaded ? `newest ${page.loaded.toLocaleString()} of ${page.total.toLocaleString()}` : `${page.total}`;
  return (
    <Box title={title} note={count}>
      <HistoryTable whoLabel={whoLabel} empty={note.empty}>{page.items.length ? page.items.map(row) : null}</HistoryTable>
      <div className="qtng-foot">
        {page.next_cursor && (
          <div className="qtng-more"><button type="button" className="qtng-btn qtng-btn--small" disabled={loadingMore} onClick={onMore}>
            {loadingMore ? 'Loading…' : 'Show more'}</button></div>
        )}
        {note.unchanged && <div className="qtng-flag">{note.unchanged}</div>}
      </div>
    </Box>
  );
}

function Parts({ api, rfq, line, who, onPicked }) {
  // The search starts with the size the line names ("568-168"), Lane 1357.
  const [q, setQ] = useState(line.search_hint || '');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const pick = async (sku) => {
    setBusy(sku || 'clear'); setError('');
    try { await api.pick(rfq.rfq_id, line.line_id, sku, rfq); await onPicked(); } catch (e) {
      setError(e.code === 'account_changed' || e.code === 'rfq_changed' ? `${e.message}` : e.message);
    } finally { setBusy(''); }
  };
  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    try { setFound((await api.searchParts(q.trim())).items || []); setError(''); } catch (err) { setError(err.message); }
  };
  // One plain reason per part (Ryan 2026-10-06): the matcher's material and
  // compound checks for same-size parts, the AI's reason for its suggestions.
  const fitText = (c, source) => (source === 'suggested' ? c.reason : c.fit_check) || '–';
  const fitClass = (c, source) => {
    if (source !== 'match') return '';
    if (c.allowed === 'yes' || (c.material === 'fits' && c.allowed !== 'maybe')) return 'qtng-yes';
    return c.material || c.allowed === 'maybe' ? 'qtng-maybe' : 'qtng-mut';
  };
  const suggested = line.suggested || [];
  const foundBy = (c, source) => (source === 'search' ? 'Your search'
    : source === 'suggested' ? SUGGESTED_FIT[c.fit] || 'Suggested'
      : FOUND_BY[c.source] || '–');
  const row = (c, source) => {
    const isSel = c.material_number === line.selected;
    const stock = c.stock || {};
    return (
      <tr key={`${source}-${c.material_number}`} className={isSel ? 'qtng-this' : ''}>
        <td className="qtng-num">{c.material_number}{isSel ? (line.needs_confirm ? ' (recommended)' : ' (this line)') : ''}</td>
        <td className="qtng-cut" style={{ maxWidth: 320 }} title={c.description || c.material_name || c.name}>{c.description || c.material_name || c.name || '–'}</td>
        <td>{foundBy(c, source)}</td>
        <td className={`qtng-cut ${fitClass(c, source)}`} style={{ maxWidth: 380 }} title={fitText(c, source)}>{fitText(c, source)}</td>
        <td>{source !== 'match' ? <span className="qtng-mut">Not checked</span>
          : c.sold_to_this_customer ? shortDate(c.last_sold_to_this_customer)
          : c.this_customer_state === 'unknown' ? <span className="qtng-mut">Could not load</span>
            : c.this_customer_state === 'never_sold' ? 'No' : '–'}</td>
        <td className="qtng-r qtng-num">{stock.state === 'in_file' ? qty(stock.on_hand)
          : stock.state === 'not_in_file' ? 'Not in file' : stock.state === 'no_file' ? 'No file'
            : stock.state === 'unknown' ? <span className="qtng-mut">Could not load</span> : '–'}</td>
        <td className="qtng-r">{isSel ? (line.needs_confirm ? (
          <button type="button" className="qtng-btn qtng-btn--small qtng-btn--primary" disabled={!!busy} onClick={() => pick(c.material_number)}>
            {busy === c.material_number ? 'Saving…' : 'Confirm'}</button>) : null) : (
          <button type="button" className="qtng-btn qtng-btn--small" disabled={!!busy} onClick={() => pick(c.material_number)}>
            {busy === c.material_number ? 'Saving…' : 'Use this'}</button>)}</td>
      </tr>
    );
  };
  const note = line.picked ? 'you picked this line\'s part'
    : line.needs_confirm ? 'Wingman recommended: please confirm'
      : line.match_basis?.fill_in === 'listed_by_customer' ? "listed in the customer's file"
        : line.selected ? 'filled in by Wingman' : 'no part selected';
  return (
    <Box title="XMEK parts for this line" note={note}>
      {line.suggested_by === 'none_fit' && (
        <div className="qtng-pad qtng-mut">Wingman searched XMEK's parts and found nothing like this line.</div>
      )}
      {line.suggested_by === 'not_picked' && (
        <div className="qtng-pad qtng-mut">Wingman could not search XMEK's parts for this line. Use the search below.</div>
      )}
      {(line.candidates.length > 0 || suggested.length > 0 || (found && found.length > 0)) && (
        <table className="qtng-t">
          <thead><tr><th>XMEK part #</th><th>Description</th><th>Found by</th><th>Fit check</th><th>{who} bought it</th><th className="qtng-r">In stock</th><th /></tr></thead>
          <tbody>{line.candidates.map((c) => row(c, 'match'))}{suggested.map((c) => row(c, 'suggested'))}{(found || []).map((c) => row(c, 'search'))}</tbody>
        </table>
      )}
      <form className="qtng-pad qtng-acctrow" onSubmit={search}>
        <input className="qtng-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find another XMEK part by number or name" aria-label="Find an XMEK part" />
        <button type="submit" className="qtng-btn" disabled={q.trim().length < 2}>Search</button>
        {found && !found.length && <span className="qtng-mut">No parts match.</span>}
        {line.picked && <button type="button" className="qtng-link" disabled={!!busy} onClick={() => pick(null)}>Clear my pick</button>}
        {error && <span className="qtng-err">{error}</span>}
      </form>
    </Box>
  );
}

// Who could make it (Lane 1391, plan 2.4): the suppliers XMEK buys this
// line's parts from, and vendor price-list parts that match it. A vendor part
// is never shown as an XMEK part.
function WhoCouldMakeIt({ line }) {
  const suppliers = line.suppliers || [];
  const vendor = line.vendor_parts || [];
  if (!suppliers.length && !vendor.length) return null;
  return (
    <Box title="Who could make it" note="suppliers XMEK buys these parts from">
      {suppliers.length > 0 && (
        <table className="qtng-t">
          <thead><tr><th>Supplier</th><th className="qtng-r">Purchase lines</th><th>Last bought</th><th className="qtng-r">Parts</th></tr></thead>
          <tbody>{suppliers.map((r) => (
            <tr key={r.supplier_name}><td className="qtng-cut" title={r.supplier_name}>{r.supplier_name}</td>
              <td className="qtng-r qtng-num">{qty(r.purchase_lines)}</td><td className="qtng-num">{shortDate(r.last_date)}</td>
              <td className="qtng-r qtng-num" title={(r.parts || []).join(', ')}>{(r.parts || []).length}</td></tr>
          ))}</tbody>
        </table>
      )}
      {vendor.length > 0 && (
        <table className="qtng-t">
          <thead><tr><th>Price list</th><th>Vendor part #</th><th>Description</th></tr></thead>
          <tbody>{vendor.map((v) => (
            <tr key={`${v.list_kind}-${v.vendor_part_number}`}><td>{LIST_NAMES[v.list_kind] || v.list_kind}</td>
              <td className="qtng-num">{v.vendor_part_number}</td><td className="qtng-cut" title={v.name}>{v.name}</td></tr>
          ))}</tbody>
        </table>
      )}
    </Box>
  );
}

function Warnings({ line, detail }) {
  // Once the quoter picked, "please confirm" is done; a pick of another part
  // also drops the spec note, which was about the recommended part (Lane 1357).
  const settled = (f) => line.picked && (f === 'part_recommended_confirm'
    || (['fill_in_check_spec_not_checked', 'fill_in_check_hardness_not_stated'].includes(f) && line.picked !== line.filled_in));
  const flags = (line.flags || []).filter((f) => !QUIET_FLAGS.has(f) && !settled(f));
  const ordered = [...FILL_FLAG_ORDER.filter((f) => flags.includes(f)), ...flags.filter((f) => !FILL_FLAG_ORDER.includes(f))];
  const items = ordered.map(lineFlagText);
  if (line.ranking_out_of_date) items.unshift('Wingman is re-checking the parts for the confirmed account.');
  if (setAsideText(detail?.xmek_cost)) items.push(setAsideText(detail.xmek_cost));
  if (detail?.stock?.old) items.push(`The inventory file is from ${shortDate(detail.stock.as_of)}.`);
  if (!items.length) return null;
  return (
    <div className="qtng-warn"><h3>Warnings</h3><ul>{items.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
  );
}

// Lane 1399: what the customer's file already gives for this item (the XMEK
// part number, a price, a lead time, a minimum order) and a link that opens
// the file at the row or page the line came from. Nothing when neither.
const GIVEN_HEADS = [['seller_part_number', 'XMEK part # listed'], ['given_price', 'Price given'],
  ['given_lead_time', 'Lead time given'], ['given_moq', 'MOQ given']];

function lineSource(rfq, line) {
  const fields = line.evidence?.fields || {};
  const segments = line.evidence?.segments || {};
  const ids = [...(fields.part_number || []), ...(fields.quantities || []), ...(fields.description || [])];
  for (const id of ids) {
    const seg = segments[id];
    const attachment = seg && (rfq.attachments || []).find((a) => a.part_path === seg.part_path && a.stored);
    if (!attachment) continue;
    // A spreadsheet row by its sheet and row; other rows by their text.
    const highlight = Object.values(segments).filter((s) => s.part_path === seg.part_path)
      .map((s) => (s.loc?.sheet != null && s.loc?.row != null ? { sheet: s.loc.sheet, row: s.loc.row } : s.text));
    return { attachment, page: seg.loc?.page || null, highlight };
  }
  return null;
}

function FromTheFile({ rfq, line, openFile }) {
  const given = line.given || {};
  const shown = GIVEN_HEADS.filter(([k]) => given[k]?.value);
  const source = lineSource(rfq, line);
  if (!shown.length && !source) return null;
  const name = source && (source.attachment.original_filename || 'the email');
  const link = source ? (
    <button type="button" className="qtng-link" onClick={() => openFile(source)}>{`Show where this line is in ${name}`}</button>) : null;
  // Only the link when the file gives nothing more for the item.
  if (!shown.length) return <div className="qtng-srcrow">{link}</div>;
  return (
    <Box title="From the customer's file" note={link}>
      <table className="qtng-t"><thead><tr>{shown.map(([k, h]) => <th key={k}>{h}</th>)}</tr></thead>
        <tbody><tr>{shown.map(([k]) => <td key={k} className="qtng-num">{given[k].value}</td>)}</tr></tbody></table>
    </Box>
  );
}

export default function QuotingLineDetail({ api, rfq, line, who, onPicked, openFile }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [more, setMore] = useState('');
  // The selection and the account revision this detail is for: an answer
  // for an earlier selection (a slow request after a new pick) or for other
  // accounts (a slow request after the accounts changed) is ignored, never
  // shown (Codex 1335, Codex 1357).
  const revision = rfq.account_revision || 0;
  const want = `${rfq.rfq_id}:${line.line_id}:${line.selected || ''}:${revision}`;
  const latest = useRef(want);
  latest.current = want;

  const load = useCallback(() => {
    const key = `${rfq.rfq_id}:${line.line_id}:${line.selected || ''}:${revision}`;
    // A different line or part: clear at once. The same one being refreshed:
    // keep showing it until the fresh answer replaces it.
    setDetail((d) => (d && d._key === key ? d : null));
    setError('');
    if (!line.selected) return;
    api.line(rfq.rfq_id, line.line_id, revision)
      .then((d) => {
        if (latest.current === key && d.selected === line.selected && (d.account_revision || 0) === revision) {
          setDetail({ ...d, _key: key });
        }
      })
      // A 409: another quoter changed the accounts. Reload the RFQ; the new
      // revision loads this line again, so it is not an error to show.
      .catch((e) => {
        if (latest.current !== key) return;
        if (e.code === 'account_changed') onPicked(); else setError(e.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, rfq.rfq_id, line.line_id, line.selected, revision, rfq._loadedAt]);
  // Reloaded on every RFQ refresh too, so an open line never keeps older
  // numbers than its row (Codex 1335).
  useEffect(() => { load(); }, [load]);

  const loadMore = async (which) => {
    const key = want;
    setMore(which);
    try {
      const used = detail[which]?.next_cursor;
      const cursor = { sold: { sold_cursor: used }, bought: { bought_cursor: used }, quoted: { quoted_cursor: used } }[which];
      const next = await api.line(rfq.rfq_id, line.line_id, revision, cursor);
      if (latest.current !== key || next.selected !== line.selected
          || (next.account_revision || 0) !== revision) return;
      // Appended only to the list the request continued: a refresh since
      // (a new export, or the same list reloaded) moved its cursor, and the
      // late page is dropped rather than mixed in or skipping a page (Codex 1357).
      setDetail((d) => (d[which]?.next_cursor !== used ? d
        : { ...d, [which]: { ...next[which], items: [...d[which].items, ...next[which].items] } }));
    } catch (e) {
      // An answer for a line or part no longer shown changes nothing.
      if (latest.current !== key) return;
      if (e.code === 'account_changed') onPicked();
      // A newer quote export was loaded meanwhile: the boxes start again.
      else if (e.code === 'quote_history_changed') load();
      else setError(e.message);
    } finally { setMore(''); }
  };

  const parts = <Parts key={line.line_id} api={api} rfq={rfq} line={line} who={who} onPicked={onPicked} />;
  const fromFile = <FromTheFile rfq={rfq} line={line} openFile={openFile} />;
  if (!line.selected) {
    return (
      <div className="qtng-dwrap">
        {fromFile}
        <div className="qtng-empty">{line.ranking_out_of_date
          ? 'Wingman is re-checking the parts for the confirmed account. This updates by itself.'
          : `${blankReason(line)} Pick a part to see its stock, costs and history.`}</div>
        {parts}
        <WhoCouldMakeIt line={line} />
        <Warnings line={line} detail={null} />
      </div>
    );
  }
  if (error) return <div className="qtng-dwrap"><div className="qtng-err">{error}</div>{parts}</div>;
  if (!detail || detail._key !== want) return <div className="qtng-dwrap"><div className="qtng-empty">Loading…</div></div>;

  const sold = detail.sold;
  const bought = detail.bought;
  const soldNote = {
    empty: 'No sales in Wingman.',
    unchanged: sold?.unchanged ? `Sale price has been ${money(sold.unchanged.unit_price)} on the last ${sold.unchanged.rows} sales, since ${shortDate(sold.unchanged.since)}, through the last sales import (${shortDate(sold.through)}).` : null,
  };
  const boughtNote = {
    empty: 'No purchases in Wingman.',
    unchanged: bought?.unchanged ? `Cost has been ${money(bought.unchanged.unit_price)} on the last ${bought.unchanged.rows} purchases, since ${shortDate(bought.unchanged.since)}.` : null,
  };
  const ourCost = detail.xmek_cost?.state === 'found' ? detail.xmek_cost.unit_cost : null;
  const hasHistory = (detail.chart?.sold || []).length || (detail.chart?.bought || []).length
    || (detail.invoice_cost_by_month || []).some((m) => m.cost_unit_price != null);
  return (
    <div className="qtng-dwrap">
      {fromFile}
      <div className="qtng-two">
        <Inventory stock={detail.stock} />
        <Vendors vendors={detail.vendors} main={line.facts?.vendor} />
      </div>
      <Costs rows={detail.cost_rows} uom={detail.unit_of_measure} xmekCost={detail.xmek_cost} />
      <PriceCalc key={want} detail={detail} line={line} who={who} />
      <div className="qtng-three">
        <History title="Bought" whoLabel="Supplier" page={bought} note={boughtNote} loadingMore={more === 'bought'} onMore={() => loadMore('bought')}
          row={(b) => (
            <tr key={b.paid_rank}><td className="qtng-num">{shortDate(b.date)}</td><td className="qtng-cut" title={b.supplier_name}>{b.supplier_name}</td>
              <td className="qtng-r qtng-num" title={qty(b.quantity)}>{qty(b.quantity)}</td>
              <td className="qtng-r qtng-num" title={b.is_outlier ? `${money(b.unit_price)}: an unusual price for this supplier` : money(b.unit_price)}>{money(b.unit_price)}{b.is_outlier ? ' *' : ''}</td></tr>
          )} />
        <Quoted page={detail.quoted} who={who} multi={(rfq.accounts || []).length > 1}
          loadingMore={more === 'quoted'} onMore={() => loadMore('quoted')} />
        <History title="Sold" whoLabel="Customer" page={sold} note={soldNote} loadingMore={more === 'sold'} onMore={() => loadMore('sold')}
          row={(s) => (
            <tr key={s.sales_order_line_id} className={s.this_account ? 'qtng-mine' : ''} title={s.this_account ? `Sold to ${who}${(rfq.accounts || []).length > 1 ? ` (account ${s.customer_code})` : ''}` : undefined}>
              <td className="qtng-num">{shortDate(s.invoice_date)}</td><td className="qtng-cut" title={s.customer_name}>{s.customer_name}</td>
              <td className="qtng-r qtng-num" title={qty(s.shipped_quantity)}>{qty(s.shipped_quantity)}</td><td className="qtng-r qtng-num" title={money(s.unit_price)}>{money(s.unit_price)}</td></tr>
          )} />
      </div>
      {parts}
      <WhoCouldMakeIt line={line} />
      {/* The chart comes after the parts and before the warnings (Ryan, 2026-09-28). */}
      {hasHistory ? (
        <Box title="Price history"><div className="qtng-pad">
          <QuotingChart chart={detail.chart} monthlyCost={detail.invoice_cost_by_month} ourCost={ourCost} who={who} />
        </div></Box>
      ) : null}
      <Warnings line={line} detail={detail} />
    </div>
  );
}
